import { InfoCircleTwoTone } from "@ant-design/icons";
import { itemOnlyTypes } from "@p0security/shared/assessment/constants";
import { ProviderOrAll } from "@p0security/shared/types/assessment";
import { AssessmentSchemaAttribute } from "@p0security/shared/types/assessment/data/index";
import {
  Button,
  Col,
  Flex,
  Input,
  Row,
  Select,
  Spacing,
  Switch,
  Typography,
} from "@p0security/ui";
import { isa } from "@p0security/util/types/is";
import { DEFAULT_SHOW_SEARCH_THRESHOLD } from "constants/antd";
import pluralize from "pluralize";
import { FilterFunc } from "rc-select/lib/Select";
import { ChangeEvent, useCallback, useMemo } from "react";

import { GraphTooltip } from "../../GraphTooltip";
import { GraphSearchProps } from "../GraphSearch";
import {
  KeytypeOption,
  termBuilderOptions as options,
  schemaValues,
  toSchemaItem,
} from "./schema";
import {
  Connection,
  EMPTY_STAGED_SEARCH,
  Keytype,
  SearchConstraint,
  buildTerm,
} from "./term-builder";
import { useStaged } from "./useStaged";

const { Paragraph, Text } = Typography;

/** Form for building query terms */
export const TermBuilderForm: React.FC<{
  // controls and provider are passed as props to ease testing
  controls: GraphSearchProps["controls"];
  provider: ProviderOrAll;
  onSubmit: (term: string) => void;
}> = ({ controls, provider, onSubmit }) => {
  const [staged, setStaged] = useStaged();
  // Type clobbering necessary due to TS compiler being overbearing here
  const schemaItem: AssessmentSchemaAttribute | undefined = useMemo(
    () => toSchemaItem(staged),
    [staged]
  );

  const validKeytypeOptions = useMemo(() => {
    const baseOptions =
      staged.connection === "are"
        ? options.keytype.filter((o) => o.value === controls.show)
        : options.keytype;

    return provider === "all"
      ? baseOptions.filter((o) => !isa(itemOnlyTypes, o.value))
      : baseOptions;
  }, [controls, provider, staged]);

  const attributeOptions = useMemo(
    () =>
      staged.keytype === "_anything"
        ? [options.attribute.key]
        : [
            options.attribute.key,
            ...schemaValues[staged.keytype].map(
              options.attribute.fromItem(provider)
            ),
          ],
    [provider, staged.keytype]
  );

  const keytermOptions = useMemo(
    () => options.keyword.fromChoices(schemaItem?.choices),
    [schemaItem]
  );

  // If querying direct relationships, can't query for '_anything';
  // in this case reset the searched type to the first available type
  const handleChangeConnection = useCallback(
    (connection: Connection) =>
      setStaged({
        ...staged,
        connection,
        first:
          connection === "are"
            ? true
            : // If keytype is show, wouldn't want an only match for reachability query
              staged.keytype === controls.show
              ? false
              : staged.first,
        keytype:
          connection === "are" ? (controls.show as Keytype) : staged.keytype,
        attribute: "_key",
        constraint: "contain",
        keyword: "",
      }),
    [controls.show, setStaged, staged]
  );

  // If querying '_anything' then attributes don't apply, so clear attribute
  // selection / update constraint selection if necessary
  const handleChangeType = useCallback(
    (keytype: Keytype) => {
      setStaged({
        ...staged,
        keytype,
        attribute: "_key",
        constraint: "contain",
        keyword: "",
      });
    },
    [setStaged, staged]
  );

  // If querying using a 'with' constraint, then select the first available attribute
  const handleChangeConstraint = useCallback(
    (constraint: SearchConstraint) => {
      setStaged({ ...staged, constraint });
    },
    [setStaged, staged]
  );

  const handleChangeAttribute = useCallback(
    (attribute: string) => {
      const base = { ...staged, attribute };
      const choices = toSchemaItem(base)?.choices;
      setStaged({
        ...base,
        constraint: choices ? "equal" : "contain",
        keyword: options.keyword.fromChoices(choices)?.at(0)?.value ?? "",
      });
    },
    [setStaged, staged]
  );

  const handleChangeKeyword = useCallback(
    (event: ChangeEvent<HTMLInputElement> | string) =>
      setStaged({
        ...staged,
        keyword: typeof event === "string" ? event : event.target.value,
      }),
    [setStaged, staged]
  );

  const toggleExclude = useCallback(
    () => setStaged({ ...staged, exclude: !staged.exclude }),
    [setStaged, staged]
  );

  const toggleInvert = useCallback(
    () => setStaged({ ...staged, invert: !staged.invert }),
    [setStaged, staged]
  );

  const toggleFirst = useCallback(
    () => setStaged({ ...staged, first: !staged.first }),
    [setStaged, staged]
  );

  // Query requires a keyword if any of:
  // - Querying all node types
  // - Querying with an exact match (with or without attribute)
  const requiresKeyword = useMemo(
    () => staged.keytype === "_anything" || staged.constraint === "equal",
    [staged]
  );

  const handleKeytypeFilter = useCallback<FilterFunc<KeytypeOption>>(
    (input: string, option) => {
      // allow for searching either grant or entitlement
      const search = input.toLowerCase();
      return !!(
        option?.value?.toLowerCase()?.includes(search) ||
        option?.label?.toLowerCase()?.includes(search)
      );
    },
    []
  );

  const submitTerm = useCallback(() => {
    const term = buildTerm(staged);
    setStaged(EMPTY_STAGED_SEARCH);
    onSubmit(term);
  }, [staged, setStaged, onSubmit]);

  return (
    <Flex gap="0.7em" vertical>
      <div>
        Show {pluralize(controls.show)} that
        <Spacing asChild ml="0.5em">
          <Select<Connection>
            onChange={handleChangeConnection}
            options={options.connect}
            /* eslint-disable-line react/forbid-component-props */ style={{
              width: "12em",
            }}
            value={staged.connection}
          />
        </Spacing>
      </div>
      {/* pl-6 makes an indentation for multi-line content */}
      <Flex align="baseline" className="w-fit pl-6" gap="0.5em" wrap>
        <Spacing asChild ml="-24px">
          <Select<Keytype, KeytypeOption>
            showSearch={
              validKeytypeOptions.length > DEFAULT_SHOW_SEARCH_THRESHOLD
            }
            filterOption={handleKeytypeFilter}
            onChange={handleChangeType}
            options={validKeytypeOptions}
            value={staged.keytype}
            disabled={staged.connection === "are"}
            // Enough room for "lateral movement paths"
            /* eslint-disable-line react/forbid-component-props */ style={{
              width: "14em",
            }}
          />
        </Spacing>
        &nbsp;with&nbsp;
        <Select
          showSearch={attributeOptions.length > DEFAULT_SHOW_SEARCH_THRESHOLD}
          onChange={handleChangeAttribute}
          options={attributeOptions}
          value={staged.attribute}
          disabled={staged.keytype === "_anything"}
          /* eslint-disable-line react/forbid-component-props */ style={{
            width: "14em",
          }}
        />
        <Select<SearchConstraint>
          onChange={handleChangeConstraint}
          options={
            schemaItem?.choices
              ? options.constraint.choices
              : options.constraint.all
          }
          disabled={!!schemaItem?.choices}
          value={staged.constraint}
          /* eslint-disable-line react/forbid-component-props */ style={{
            width: "8em",
          }}
        />
        {keytermOptions ? (
          <Select<string>
            showSearch={keytermOptions.length > DEFAULT_SHOW_SEARCH_THRESHOLD}
            onChange={handleChangeKeyword}
            options={keytermOptions}
            value={staged.keyword}
            /* eslint-disable-line react/forbid-component-props */ style={{
              width: "18em",
            }}
            data-testid="keyword-select"
          />
        ) : (
          <Spacing asChild mb={0}>
            <Input
              value={staged.keyword}
              onChange={handleChangeKeyword}
              placeholder={`Enter a search term${
                requiresKeyword ? "" : ", or leave empty to match any value"
              }`}
              /* eslint-disable-line react/forbid-component-props */ style={{
                width: "28em",
              }}
              onPressEnter={submitTerm}
            />
          </Spacing>
        )}
        <Row
          /* eslint-disable-line react/forbid-component-props */ style={{
            width: "20em",
          }}
        >
          <Col span={12}>
            Remove match?&nbsp;
            <GraphTooltip
              title={
                <>
                  <Paragraph>
                    Select to remove any results that match your query.
                  </Paragraph>
                  <Paragraph>
                    For example, removing &quot;group&quot; identity types will
                    hide any identity that belongs to a group, including
                    individual users.
                  </Paragraph>
                </>
              }
            >
              <InfoCircleTwoTone />
            </GraphTooltip>
          </Col>
          <Col span={12}>
            <Switch checked={staged.exclude} onChange={toggleExclude} />
          </Col>
          <Col span={12}>
            <label>
              Invert match?&nbsp;
              <GraphTooltip
                title={
                  <>
                    <Paragraph>
                      Select to return results that do not match your search
                      term.
                    </Paragraph>
                    <Paragraph>
                      For example, inverting &quot;group&quot; identity types
                      will remove group identities, but still return user
                      identities, even if those users belong to groups.
                    </Paragraph>
                  </>
                }
              >
                <InfoCircleTwoTone />
              </GraphTooltip>
            </label>
          </Col>
          <Col span={12}>
            <Switch checked={staged.invert} onChange={toggleInvert} />
          </Col>
          <Col span={12}>
            <label>
              Only first node?&nbsp;
              <GraphTooltip
                title={
                  <>
                    <Paragraph>
                      Select to only return a result if the first type in a
                      relationship chain matches your query.
                    </Paragraph>
                    <Paragraph>
                      For example, searching for only the first identity
                      matching a group name would return only that group. With
                      this option unselected, results would contain all
                      identities that are either members of the group, or of
                      which the group is a member.
                    </Paragraph>
                  </>
                }
              >
                <InfoCircleTwoTone />
              </GraphTooltip>
            </label>
          </Col>
          <Col span={12}>
            <Switch
              checked={staged.first}
              disabled={staged.connection === "are"}
              onChange={toggleFirst}
            />
          </Col>
        </Row>
      </Flex>
      <Flex align="baseline" gap="0.5em" justify="space-between" wrap>
        <Text type="secondary">{buildTerm(staged)}</Text>
        <Button
          type="primary"
          disabled={requiresKeyword && !staged.keyword}
          onClick={submitTerm}
        >
          Add term
        </Button>
      </Flex>
    </Flex>
  );
};
